import { useEffect, useMemo, useState } from "react";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Clock, ChevronDown, ChevronUp, Ticket } from "lucide-react";
import { useI18n } from "../i18n";

const FILTERS = [
  { key: "all", label_key: "filter_all" },
  { key: "kz", label_key: "filter_kz" },
  { key: "europe", label_key: "filter_europe" },
  { key: "ca", label_key: "filter_ca" },
  { key: "us", label_key: "filter_us" },
];

const formatDate = (iso, lang) => {
  const d = new Date(iso);
  const locale = lang === "ru" ? "ru-RU" : "en-US";
  const day = d.toLocaleDateString(locale, { day: "2-digit" });
  const month = d.toLocaleDateString(locale, { month: "short" });
  const weekday = d.toLocaleDateString(locale, { weekday: "short" });
  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return {
    day: day,
    month: month.toUpperCase(),
    weekday: weekday.toUpperCase(),
    time,
  };
};

const StatusBadge = ({ status, t }) => {
  if (status === "sold_out") {
    return (
      <span className="text-[10px] tracking-widest font-semibold text-red-400 border border-red-400/50 px-2 py-1 uppercase">
        {t.tour.sold_out}
      </span>
    );
  }
  if (status === "low_tickets") {
    return (
      <span className="text-[10px] tracking-widest font-semibold text-[#FFDE00] border border-[#FFDE00]/50 px-2 py-1 uppercase">
        {t.tour.low}
      </span>
    );
  }
  return null;
};

const ShowCard = ({ show, t, lang, index, past = false }) => {
  const { day, month, weekday, time } = formatDate(show.date, lang);
  const isSold = show.status === "sold_out";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      className={`group relative border bg-black/55 backdrop-blur-md transition-colors p-3 sm:p-5 md:p-6 ${
        past
          ? "border-white/10 grayscale opacity-55 hover:opacity-80"
          : "border-white/15 hover:border-[#FFDE00]/70 hover:bg-black/70"
      }`}
      data-testid={`tour-row-${show.id}`}
    >
      {/* Top strip: date + status */}
      <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
        <div className="flex items-baseline gap-1.5 sm:gap-2">
          <span className="font-display text-4xl sm:text-5xl md:text-6xl leading-none tracking-tighter">
            {day}
          </span>
          <div className="flex flex-col items-start">
            <span
              className={`font-display text-sm sm:text-lg md:text-xl leading-none ${
                past ? "text-white/40" : "text-[#FFDE00]"
              }`}
            >
              {month}
            </span>
            <span className="text-[9px] sm:text-[10px] tracking-widest text-white/45 mt-1">
              {weekday}
            </span>
          </div>
        </div>
        {past ? (
          <span
            className="text-[9px] sm:text-[10px] tracking-widest font-semibold text-white/45 border border-white/15 px-2 py-1 uppercase"
            data-testid={`tour-past-${show.id}`}
          >
            {t.tour.past_badge}
          </span>
        ) : (
          <StatusBadge status={show.status} t={t} />
        )}
      </div>

      {/* City + country */}
      <div className="font-display text-xl sm:text-3xl md:text-4xl tracking-tighter leading-none break-words">
        {show.city}
      </div>
      <div className="text-[10px] sm:text-[11px] tracking-widest text-white/45 mt-1.5 sm:mt-2 flex items-center gap-1 sm:gap-1.5 uppercase">
        <MapPin size={10} /> {show.country}
      </div>

      {/* Venue */}
      {(show.venue || !past) && (
        <div className="mt-3 sm:mt-5 text-xs sm:text-sm md:text-base text-white/90 font-medium leading-snug">
          {show.venue || t.tour.tba}
        </div>
      )}
      {show.venue_address ? (
        <div className="text-[10px] sm:text-xs text-white/45 mt-1 leading-snug">{show.venue_address}</div>
      ) : (
        !past && (
          <div className="text-[9px] sm:text-[10px] text-white/35 mt-1 leading-snug italic">{t.tour.tba_note}</div>
        )
      )}

      {/* Time + CTA */}
      <div className="mt-4 sm:mt-6 pt-3 sm:pt-5 border-t border-white/10 flex flex-col gap-3">
        <span className="text-[10px] sm:text-[11px] tracking-widest text-white/50 flex items-center gap-1 sm:gap-1.5 uppercase">
          <Clock size={10} /> {t.tour.doors} · {time}
        </span>
        {past ? null : !isSold && show.ticket_url ? (
          <a
            href={show.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`tour-cta-${show.id}`}
            className="cta-tickets group/cta relative flex items-center justify-center gap-2 w-full bg-[#FFDE00] text-black px-3 sm:px-4 py-3 sm:py-3.5 text-xs sm:text-sm tracking-widest font-extrabold uppercase border border-[#FFDE00] hover:bg-[#FFE833] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 shadow-[0_0_0_0_rgba(255,222,0,0)] hover:shadow-[0_10px_28px_-6px_rgba(255,222,0,0.55)]"
          >
            <Ticket size={15} className="shrink-0" />
            <span>{t.tour.get_tickets}</span>
            <ArrowUpRight
              size={15}
              className="shrink-0 transition-transform duration-200 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
            />
          </a>
        ) : !isSold ? (
          <span
            className="flex items-center justify-center gap-2 w-full text-[10px] sm:text-[11px] tracking-widest text-white/55 uppercase border border-white/15 bg-black/40 px-3 py-3 font-semibold"
            data-testid={`tour-tba-${show.id}`}
          >
            <Ticket size={13} className="text-[#FFDE00]/70" /> {t.tour.tba}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2 w-full text-[10px] sm:text-[11px] tracking-widest text-red-300 uppercase border border-red-500/40 bg-red-500/10 px-3 py-3 font-bold">
            {t.tour.sold_out}
          </span>
        )}
      </div>
    </motion.div>
  );
};

const TourSchedule = () => {
  const { t, lang } = useI18n();
  const [shows, setShows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [archiveOpen, setArchiveOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get("/shows", {
          params: filter === "all" ? {} : { region: filter },
        });
        if (!cancel) setShows(res.data);
      } catch (e) {
        console.error("Failed to load shows", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [filter]);

  const { upcoming, past, pastByYear } = useMemo(() => {
    const now = Date.now();
    const up = [];
    const ar = [];
    for (const s of shows) {
      const ts = new Date(s.date).getTime();
      if (ts + 6 * 60 * 60 * 1000 < now) ar.push(s);
      else up.push(s);
    }
    up.sort((a, b) => new Date(a.date) - new Date(b.date));
    ar.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Group past shows by year, preserving descending order
    const byYear = {};
    for (const s of ar) {
      const y = new Date(s.date).getFullYear();
      if (!byYear[y]) byYear[y] = [];
      byYear[y].push(s);
    }
    const yearsDesc = Object.keys(byYear)
      .map(Number)
      .sort((a, b) => b - a);
    const grouped = yearsDesc.map((y) => ({ year: y, shows: byYear[y] }));

    return { upcoming: up, past: ar, pastByYear: grouped };
  }, [shows]);

  return (
    <section id="tour" data-testid="tour-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Strong red theatrical backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 20% 20%, rgba(225,29,46,0.32) 0%, transparent 60%)," +
            "radial-gradient(ellipse 60% 50% at 80% 80%, rgba(190,18,30,0.30) 0%, transparent 65%)," +
            "linear-gradient(180deg, rgba(40,4,8,0.55) 0%, rgba(10,0,2,0.85) 100%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <div>
            <div className="kicker mb-4">{t.tour.kicker}</div>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
              data-testid="tour-title"
            >
              {t.tour.title}
            </h2>
            <p className="mt-4 text-white/65 max-w-md">{t.tour.subtitle}</p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="tour-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`tour-filter-${f.key}`}
                className={`px-4 py-2 text-xs tracking-widest uppercase font-semibold border transition-all ${
                  filter === f.key
                    ? "bg-[#FFDE00] text-black border-[#FFDE00]"
                    : "text-white/70 border-white/15 hover:text-white hover:border-white/40"
                }`}
              >
                {t.tour[f.label_key]}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="py-16 text-center text-white/40 text-sm tracking-widest uppercase">
            Loading...
          </div>
        )}
        {!loading && upcoming.length === 0 && past.length === 0 && (
          <div className="py-16 text-center text-white/40 text-sm tracking-widest uppercase">
            No shows
          </div>
        )}

        {!loading && upcoming.length > 0 && (
          <div
            className="grid grid-cols-2 gap-3 md:gap-4"
            data-testid="tour-list"
          >
            {upcoming.map((s, idx) => (
              <ShowCard key={s.id} show={s} t={t} lang={lang} index={idx} />
            ))}
          </div>
        )}

        {!loading && past.length > 0 && (
          <div className="mt-16 md:mt-20" data-testid="tour-archive">
            <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
              <div>
                <div className="kicker mb-3 !text-white/40 [&::before]:!bg-white/30">
                  {t.tour.past_title}
                </div>
                <h3 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter text-white/70">
                  {t.tour.past_title}
                </h3>
                <p className="mt-2 text-white/40 text-sm">{t.tour.past_subtitle}</p>
              </div>
              <span className="text-xs tracking-widest text-white/40 uppercase">
                {past.length}
              </span>
            </div>

            {/* Preview — 2 first past shows, only when archive is collapsed */}
            {!archiveOpen && (
              <div className="grid grid-cols-2 gap-3 md:gap-4" data-testid="tour-past-preview">
                {past.slice(0, 2).map((s, idx) => (
                  <ShowCard key={s.id} show={s} t={t} lang={lang} index={idx} past />
                ))}
              </div>
            )}

            {/* Expandable archive grouped by year */}
            {past.length > 2 && (
              <>
                <AnimatePresence initial={false}>
                  {archiveOpen && (
                    <motion.div
                      key="archive-rest"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-10 md:space-y-14" data-testid="tour-past-list">
                        {pastByYear.map(({ year, shows: yearShows }) => (
                          <div
                            key={year}
                            data-testid={`tour-past-year-${year}`}
                          >
                            <div className="flex items-end justify-between gap-3 mb-4 md:mb-5 pb-3 border-b border-white/10">
                              <h4 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter text-white/35">
                                {year}
                              </h4>
                              <span className="text-[11px] tracking-widest text-white/35 uppercase font-semibold">
                                {yearShows.length}{" "}
                                {lang === "ru" ? "шоу" : "shows"}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 md:gap-4">
                              {yearShows.map((s, idx) => (
                                <ShowCard
                                  key={s.id}
                                  show={s}
                                  t={t}
                                  lang={lang}
                                  index={idx}
                                  past
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-6 md:mt-8 flex justify-center">
                  <button
                    onClick={() => setArchiveOpen((v) => !v)}
                    data-testid="tour-past-toggle"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 hover:border-[#FFDE00]/70 hover:text-[#FFDE00] transition-colors text-xs tracking-widest uppercase font-semibold"
                  >
                    {archiveOpen ? (
                      <>
                        <ChevronUp size={14} /> {t.tour.past_show_less}
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} /> {t.tour.past_show_more}{" "}
                        <span className="text-[#FFDE00]/80">({past.length - 2})</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default TourSchedule;
